import React, { useState, useEffect } from 'react';
import { Accessibility, X, Plus, Minus, RotateCcw, Type, MoveHorizontal, Contrast, Link as LinkIcon, ExternalLink } from 'lucide-react';

const STORAGE_KEY = 'cfl-a11y-prefs';

const DEFAULTS = { fontScale: 100, spacing: 0, highContrast: false, underlineLinks: false };

function applyPrefs(prefs) {
    const root = document.documentElement;
    root.style.setProperty('--a11y-font-scale', `${prefs.fontScale}%`);
    root.style.setProperty('--a11y-letter-spacing', `${prefs.spacing}px`);
    root.classList.toggle('a11y-high-contrast', prefs.highContrast);
    root.classList.toggle('a11y-underline-links', prefs.underlineLinks);
}

export default function AccessibilityWidget() {
    const [open, setOpen] = useState(false);
    const [showStatement, setShowStatement] = useState(false);
    const [prefs, setPrefs] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
        } catch { return DEFAULTS; }
    });

    useEffect(() => {
        applyPrefs(prefs);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch { /* no-op */ }
    }, [prefs]);

    useEffect(() => {
        if (!open && !showStatement) return;
        const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); setShowStatement(false); } };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, showStatement]);

    const update = (patch) => setPrefs((p) => ({ ...p, ...patch }));
    const reset = () => setPrefs(DEFAULTS);

    return (
        <>
            {/* Floating button */}
            <button
                type="button"
                data-testid="a11y-toggle-button"
                onClick={() => setOpen(true)}
                aria-label="Open accessibility options"
                className="fixed bottom-5 left-5 z-[60] inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-navy text-white shadow-[0_10px_30px_-5px_rgba(31,27,77,0.5)] hover:bg-brand-orange focus:outline-none focus:ring-4 focus:ring-brand-orange/40 transition-colors"
            >
                <Accessibility className="w-7 h-7" strokeWidth={2} />
                <span className="sr-only">Accessibility</span>
            </button>

            {/* Panel */}
            {open && (
                <div
                    role="dialog"
                    aria-label="Accessibility options"
                    data-testid="a11y-panel"
                    className="fixed inset-0 z-[70] flex items-end sm:items-center sm:justify-start"
                >
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={() => setOpen(false)}
                        className="absolute inset-0 bg-brand-navy/40 backdrop-blur-sm"
                    />
                    <div className="relative w-full sm:max-w-md sm:m-5 sm:ml-5 bg-white shadow-2xl sm:rounded-lg overflow-hidden">
                        <header className="flex items-center justify-between px-5 py-4 bg-brand-navy text-white">
                            <div className="flex items-center gap-3">
                                <Accessibility className="w-5 h-5 text-brand-orange" />
                                <span className="font-display font-extrabold text-lg">Accessibility</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                aria-label="Close accessibility options"
                                data-testid="a11y-close-button"
                                className="inline-flex h-9 w-9 items-center justify-center hover:bg-white/10 rounded"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </header>

                        <div className="p-5 space-y-5 max-h-[80vh] overflow-y-auto">
                            {/* Text size */}
                            <Control
                                icon={Type}
                                title="Text size"
                                value={`${prefs.fontScale}%`}
                                onMinus={() => update({ fontScale: Math.max(80, prefs.fontScale - 10) })}
                                onPlus={() => update({ fontScale: Math.min(160, prefs.fontScale + 10) })}
                                testid="a11y-font-scale"
                            />
                            {/* Letter spacing */}
                            <Control
                                icon={MoveHorizontal}
                                title="Letter spacing"
                                value={`+${prefs.spacing}px`}
                                onMinus={() => update({ spacing: Math.max(0, prefs.spacing - 1) })}
                                onPlus={() => update({ spacing: Math.min(6, prefs.spacing + 1) })}
                                testid="a11y-letter-spacing"
                            />

                            {/* Toggles */}
                            <Toggle
                                icon={Contrast}
                                title="High contrast"
                                enabled={prefs.highContrast}
                                onChange={(v) => update({ highContrast: v })}
                                testid="a11y-high-contrast"
                            />
                            <Toggle
                                icon={LinkIcon}
                                title="Underline links"
                                enabled={prefs.underlineLinks}
                                onChange={(v) => update({ underlineLinks: v })}
                                testid="a11y-underline-links"
                            />

                            {/* Reset */}
                            <button
                                type="button"
                                onClick={reset}
                                data-testid="a11y-reset-button"
                                className="w-full inline-flex items-center justify-center gap-2 border border-brand-navy/20 text-brand-navy hover:bg-brand-navy hover:text-white px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
                            >
                                <RotateCcw className="w-4 h-4" /> Reset
                            </button>

                            {/* Statement link */}
                            <button
                                type="button"
                                data-testid="a11y-statement-button"
                                onClick={() => { setShowStatement(true); setOpen(false); }}
                                className="w-full inline-flex items-center justify-between gap-2 bg-brand-orange hover:bg-brand-navy text-white px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
                            >
                                Accessibility statement <ExternalLink className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showStatement && (
                <AccessibilityStatement onClose={() => setShowStatement(false)} />
            )}
        </>
    );
}

function Control({ icon: Icon, title, value, onMinus, onPlus, testid }) {
    return (
        <div className="flex items-center gap-4 border border-brand-navy/10 p-3">
            <Icon className="w-5 h-5 text-brand-orange shrink-0" />
            <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-navy/55 font-bold">{title}</div>
                <div className="font-display font-extrabold text-brand-navy">{value}</div>
            </div>
            <button
                type="button"
                onClick={onMinus}
                aria-label={`${title} decrease`}
                data-testid={`${testid}-minus`}
                className="h-9 w-9 inline-flex items-center justify-center border border-brand-navy/15 hover:bg-brand-navy hover:text-white text-brand-navy"
            >
                <Minus className="w-4 h-4" />
            </button>
            <button
                type="button"
                onClick={onPlus}
                aria-label={`${title} increase`}
                data-testid={`${testid}-plus`}
                className="h-9 w-9 inline-flex items-center justify-center border border-brand-navy/15 hover:bg-brand-navy hover:text-white text-brand-navy"
            >
                <Plus className="w-4 h-4" />
            </button>
        </div>
    );
}

function Toggle({ icon: Icon, title, enabled, onChange, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={() => onChange(!enabled)}
            aria-pressed={enabled}
            className="w-full flex items-center gap-4 border border-brand-navy/10 p-3 hover:border-brand-orange transition-colors"
        >
            <Icon className="w-5 h-5 text-brand-orange shrink-0" />
            <div className="flex-1 text-left">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-navy/55 font-bold">{title}</div>
                <div className="font-display font-extrabold text-brand-navy">{enabled ? 'On' : 'Off'}</div>
            </div>
            <span className={`relative inline-block h-6 w-11 transition-colors ${enabled ? 'bg-brand-orange' : 'bg-brand-navy/20'}`}>
                <span className={`absolute top-0.5 left-0.5 h-5 w-5 bg-white transition-transform ${enabled ? 'translate-x-5' : ''}`} />
            </span>
        </button>
    );
}

function AccessibilityStatement({ onClose }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div
            role="dialog"
            aria-label="Website Accessibility Statement"
            data-testid="a11y-statement-dialog"
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-8"
        >
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 bg-brand-navy/60 backdrop-blur-sm"
            />
            <div className="relative bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                <header className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-brand-navy text-white">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-orange font-bold">CFL Ad Group</div>
                        <h2 className="font-display font-extrabold text-xl">Website Accessibility Statement</h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close statement"
                        data-testid="a11y-statement-close"
                        className="inline-flex h-9 w-9 items-center justify-center hover:bg-white/10"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </header>

                <div className="p-6 md:p-10 text-brand-navy/85 leading-relaxed space-y-6 text-[15px]">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-orange font-bold">
                        Last Updated: February 27, 2026
                    </p>

                    <Section title="Our Commitment to Accessibility">
                        We are committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying relevant accessibility standards to enhance usability and inclusivity. Our goal is to provide a website that is accessible to the widest possible audience, regardless of technology or ability.
                    </Section>

                    <Section title="Accessibility Standards">
                        We strive to conform to <strong>WCAG 2.1 Level AA</strong> — the Web Content Accessibility Guidelines published by the W3C, widely recognized as the industry standard for website accessibility. Accessibility is an ongoing process, and we are actively working toward improving the accessibility and usability of our website.
                    </Section>

                    <Section title="Accessibility Technology & Tools">
                        <p>
                            To support our accessibility efforts, we have deployed industry-standard accessibility technology designed to enhance usability and assist visitors with disabilities. These tools may provide features such as:
                        </p>
                        <ul className="list-disc pl-6 space-y-1 mt-3">
                            <li>Screen reader enhancements</li>
                            <li>Keyboard navigation improvements</li>
                            <li>Color contrast adjustments</li>
                            <li>Text size and spacing controls</li>
                            <li>Focus indicators</li>
                            <li>Additional accessibility interface options</li>
                        </ul>
                        <p className="mt-3">
                            This technology helps us improve accessibility while we continue broader auditing, remediation, and development efforts.
                        </p>
                    </Section>

                    <Section title="Ongoing Improvement">
                        <p>Accessibility is an ongoing initiative. We are:</p>
                        <ul className="list-disc pl-6 space-y-1 mt-3">
                            <li>Reviewing site content and functionality regularly</li>
                            <li>Working with developers and accessibility tools</li>
                            <li>Identifying and remediating barriers</li>
                            <li>Incorporating accessibility into future design and development</li>
                        </ul>
                        <p className="mt-3">
                            We understand accessibility is a continuous journey and are committed to making improvements over time.
                        </p>
                    </Section>

                    <Section title="Third-Party Content">
                        Some areas of the website may include third-party content, plugins, or integrations that are not fully controlled by us. While we encourage vendors to provide accessible content, we cannot guarantee accessibility of third-party features. However, we welcome feedback regarding any issues encountered.
                    </Section>

                    <Section title="Feedback & Assistance">
                        <p>
                            If you experience difficulty accessing any part of this website, need assistance, or would like to request content in an alternative format, please contact us.
                        </p>
                        <p className="mt-3">Please include:</p>
                        <ul className="list-disc pl-6 space-y-1 mt-2">
                            <li>The page URL</li>
                            <li>Description of the issue</li>
                            <li>Your preferred contact method</li>
                        </ul>
                        <p className="mt-3">
                            Contact: see our{' '}
                            <a href="/contact" className="text-brand-orange font-semibold underline hover:text-brand-navy">Contact Us</a> page.
                        </p>
                        <p className="mt-3">
                            We aim to respond within a reasonable timeframe and will make reasonable efforts to provide accessible alternatives.
                        </p>
                    </Section>

                    <Section title="Good Faith Efforts">
                        We are making good-faith efforts to comply with accessibility standards and improve usability for individuals with disabilities. Despite our efforts, some content may not yet fully meet the strictest accessibility standards due to the evolving nature of technology and ongoing updates. We appreciate your understanding as we continue improving accessibility.
                    </Section>

                    <Section title="Terms & Conditions — Accessibility Clause">
                        We are committed to providing an accessible website experience for all users and strive to follow recognized accessibility standards, including WCAG guidelines. We have implemented industry-standard accessibility tools and continue to review, update, and improve our website to enhance accessibility. Because accessibility is an ongoing process, some areas of the website may not yet fully conform to all accessibility standards. If you encounter accessibility barriers, please contact us so we can provide assistance, offer alternative access, and address the issue where feasible. Nothing in these Terms is intended to limit any rights available under applicable accessibility laws.
                    </Section>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <h3 className="font-display font-extrabold text-xl text-brand-navy mb-2">{title}</h3>
            <div className="text-brand-navy/80">{children}</div>
        </section>
    );
}
